import React, { useEffect, useRef, useState } from 'react';
import { StarIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const Message: React.FC = () => {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <section 
            className="relative py-24 sm:py-32 px-4 overflow-hidden" 
            ref={sectionRef}
        >
            {/* 背景装饰 */}
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/90 to-blue-600/90 backdrop-blur-sm"></div>
            
            <motion.div 
                className="relative z-10 max-w-5xl mx-auto rounded-3xl overflow-hidden shadow-2xl backdrop-blur-md bg-white/10 p-8 border border-white/20"
                initial={{ opacity: 0, y: 50 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.6, delay: 0.2 }}
            >
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/10 to-transparent pointer-events-none"></div>
                
                {/* 标题部分 */}
                <div className="text-center">
                    <motion.h2 
                        className="text-base font-bold leading-7 text-white bg-teal-600/80 inline-block px-4 py-1 rounded-full shadow-lg"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        {t('message.title')}
                    </motion.h2>
                    <motion.p 
                        className="mt-6 text-3xl font-extrabold tracking-tight text-white sm:text-4xl drop-shadow-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                    >
                        {t('message.subtitle')}
                    </motion.p>
                </div>

                {/* 内容部分 */}
                <motion.figure 
                    className="mt-10"
                    initial={{ opacity: 0 }}
                    animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                >
                    <blockquote className="text-center text-xl font-semibold leading-8 text-white sm:text-2xl sm:leading-9">
                        <p className="drop-shadow">
                            "{t('message.content.title')}"
                        </p>
                        <p className="mt-8 text-base text-white/80">
                            {t('message.content.description')}
                        </p>
                    </blockquote>
                    <figcaption className="mt-10">
                        <div className="mt-4 flex items-center justify-center space-x-3 text-base">
                            <StarIcon className="h-8 w-8 text-yellow-300 animate-pulse" aria-hidden="true" />
                            <div className="font-semibold text-white drop-shadow">
                                {t('message.content.highlight')}
                            </div>
                        </div>
                    </figcaption>
                </motion.figure>
            </motion.div>
        </section>
    );
};

export default Message; 